import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { HttpClient, APIResponseType } from './types';
import { handleLogout, header } from 'utils/auth';


export class AxiosAdapter implements HttpClient {
  private axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Interceptors for request and response
  constructor() {
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Inject token dynamically from header()
        if (header())
          config.headers['Authorization'] = header();

        // IMPORTANT: For FormData, we need to let Axios set the Content-Type header with proper boundary
        if (config.data instanceof FormData) {
          // Delete any Content-Type header so Axios can set it automatically with the boundary
          delete config.headers['Content-Type'];
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    this.axiosInstance.interceptors.response.use(
      response => response,
      error => {
        // if it's a "jwt expired" case, force a logout
        if (
          error.response &&
          error.response.status === 400 &&
          error.response.data?.message === 'Access Denied. jwt expired'
        ) {
          handleLogout();
        }
        return this.handleError(error);
      }
    );
  }

  // Method to handle errors
  private handleError(error: any): Promise<never> {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Response Error:', error.response.data);
      return Promise.reject({
        message: error.response.data.message || 'An error occurred',
        statusCode: error.response.status,
        statusText: error.response.statusText,
        // Preserve original error response data for error extraction
        response: error.response,
        responseData: error.response.data,
        originalError: error
      });
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Request Error:', error.request);
      return Promise.reject({
        message: 'No response received from the server',
        originalError: error
      });
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error:', error.message);
      return Promise.reject({
        message: error.message,
        originalError: error
      });
    }
  }

  // Generic method to make GET requests
  async get<T>(url: string, config?: Record<string, any>): Promise<APIResponseType<T>> {
    try {
      const response: AxiosResponse<APIResponseType<T>> = await this.axiosInstance.get(url, config);
      
      // Handle blob responses specially to extract filename from Content-Disposition header
      if (config?.responseType === 'blob' || response.data instanceof Blob) {
        let filename: string | undefined;
        
        if (response.headers && response.headers['content-disposition']) {
          const contentDisposition = response.headers['content-disposition'];
          const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }
        
        return {
          body: response.data as unknown as T,
          statusCode: response.status,
          statusText: response.statusText,
          message: response.statusText,
          filename: filename
        };
      }
      
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Generic method to make POST requests
  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponseType<T>> {
    try {
      const response: AxiosResponse<APIResponseType<T>> = await this.axiosInstance.post(url, data, config);
      
      // Handle blob responses specially to extract filename from Content-Disposition header
      if (config?.responseType === 'blob' || response.data instanceof Blob) {
        let filename: string | undefined;
        
        if (response.headers && response.headers['content-disposition']) {
          const contentDisposition = response.headers['content-disposition'];
          const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }
        
        return {
          body: response.data as unknown as T,
          statusCode: response.status,
          statusText: response.statusText,
          message: response.statusText,
          filename: filename
        };
      }
      
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Generic method to make PUT requests
  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponseType<T>> {
    try {
      const response: AxiosResponse<APIResponseType<T>> = await this.axiosInstance.put(url, data, config);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }

  // Generic method to make DELETE requests
  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<APIResponseType<T>> {
    try {
      const response: AxiosResponse<APIResponseType<T>> = await this.axiosInstance.delete(url, config);
      return response.data;
    } catch (error) {
      return this.handleError(error);
    }
  }
}